import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Info, Loader2, Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { formatHeure } from '@/lib/heure'
import { useProfile } from '@/api/profile'
import {
  useCompanySchedule,
  useUpdateCompanySchedule,
  type CompanySchedule,
} from '@/api/company'

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const
type WeekdayKey = (typeof WEEKDAYS)[number]

type Mode = 'always-open' | 'always-closed' | 'custom'

// =============================================================================
// Schéma Zod — source de vérité du formulaire (= shape du payload backend).
// =============================================================================
const dayHoursSchema = z.object({
  open: z.string(),
  close: z.string(),
})

const scheduleSchema = z.object({
  open_message: z.string(),
  closed_message: z.string(),
  days: z.object({
    Mon: dayHoursSchema,
    Tue: dayHoursSchema,
    Wed: dayHoursSchema,
    Thu: dayHoursSchema,
    Fri: dayHoursSchema,
    Sat: dayHoursSchema,
    Sun: dayHoursSchema,
  }),
})

type FormValues = z.infer<typeof scheduleSchema>

// =============================================================================
// Options heures (HH:MM par tranche 30 min, affichées dans la langue de l'interface).
// =============================================================================
const TIME_OPTIONS = (() => {
  const out: string[] = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
    }
  }
  return out
})()

// =============================================================================
// Helpers — détecte le mode à partir de `days`, et fabrique des jours par défaut.
// =============================================================================
function detectMode(days: FormValues['days']): Mode {
  const allEmpty = WEEKDAYS.every((d) => !days[d].open && !days[d].close)
  if (allEmpty) return 'always-open'
  const allZero = WEEKDAYS.every(
    (d) => days[d].open === '00:00' && days[d].close === '00:00',
  )
  if (allZero) return 'always-closed'
  return 'custom'
}

const EMPTY_DAY = { open: '', close: '' }
const CLOSED_DAY = { open: '00:00', close: '00:00' }
const DEFAULT_OPEN_DAY = { open: '09:00', close: '17:00' }

function daysFromMode(mode: Mode): FormValues['days'] {
  const template =
    mode === 'always-open'
      ? EMPTY_DAY
      : mode === 'always-closed'
        ? CLOSED_DAY
        : DEFAULT_OPEN_DAY
  return WEEKDAYS.reduce(
    (acc, d) => ({ ...acc, [d]: { ...template } }),
    {} as FormValues['days'],
  )
}

const FALLBACK_DEFAULTS: FormValues = {
  open_message: '',
  closed_message: '',
  days: daysFromMode('custom'),
}

// =============================================================================
// Composant
// =============================================================================
export function ScheduleView() {
  const { t, i18n } = useTranslation()
  const { data: profile } = useProfile()
  const companyId = profile?.company

  const { data, isLoading } = useCompanySchedule(companyId)
  const updateSchedule = useUpdateCompanySchedule(companyId)

  const form = useForm<FormValues>({
    resolver: zodResolver(scheduleSchema),
    defaultValues: FALLBACK_DEFAULTS,
  })
  const { control, register, handleSubmit, setValue, reset, formState } = form

  // Re-sync form quand le backend renvoie une nouvelle version (premier load ou invalidation).
  useEffect(() => {
    if (data) reset(data)
  }, [data, reset])

  // Watch des `days` pour dériver `mode` et l'état actif de chaque jour à la volée.
  const days = useWatch({ control, name: 'days' })

  const mode = useMemo<Mode>(() => detectMode(days), [days])

  const setMode = (next: Mode) => {
    setValue('days', daysFromMode(next), {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  const isDayActive = (day: WeekdayKey) =>
    !!days[day]?.open && days[day].open !== ''

  const toggleDay = (day: WeekdayKey) => {
    const target = isDayActive(day) ? EMPTY_DAY : DEFAULT_OPEN_DAY
    setValue(`days.${day}`, { ...target }, { shouldDirty: true })
  }

  const onSubmit = async (values: FormValues) => {
    try {
      await updateSchedule.mutateAsync(values as CompanySchedule)
      // Resync le baseline RHF pour repasser isDirty à false.
      reset(values)
      toast.success(t('manager.schedule.toast.saved'))
    } catch {
      toast.error(t('manager.schedule.toast.error'))
    }
  }

  const modes: Array<{ value: Mode; label: string }> = [
    { value: 'always-open', label: t('manager.schedule.alwaysOpen') },
    { value: 'always-closed', label: t('manager.schedule.alwaysClosed') },
    { value: 'custom', label: t('manager.schedule.customHours') },
  ]

  const isSubmitting = formState.isSubmitting || updateSchedule.isPending
  // Bouton Save désactivé tant qu'aucun champ n'est dirty (pattern RHF).
  const canSave = formState.isDirty && !isSubmitting

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="h-full flex flex-col"
    >
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-6 lg:px-8 py-6 space-y-4 sm:space-y-6">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
                {t('manager.schedule.sectionTitle')}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {t('manager.schedule.sectionSubtitle')}
              </p>
            </div>
            <Button
              type="submit"
              disabled={!canSave}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {isSubmitting
                ? t('manager.schedule.saving')
                : t('common.save')}
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              {/* Info banner */}
              <div className="bg-blue-50/80 dark:bg-blue-950/20 border border-blue-200/50 dark:border-blue-800/30 rounded-lg p-3 flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                <p>{t('manager.schedule.numbersInfo', "Ces heures valent pour tous vos numéros. Un numéro peut avoir les siennes depuis sa page.")}</p>
              </div>

              {/* Mode picker */}
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
                  {t('manager.schedule.availabilityMode')}
                </h3>
                <div className="inline-flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg border border-slate-200 dark:border-slate-700">
                  {modes.map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => setMode(m.value)}
                      className={cn(
                        'px-4 py-1.5 rounded-md text-sm font-medium transition-all',
                        mode === m.value
                          ? m.value === 'custom'
                            ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
                            : 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                          : 'text-slate-600 dark:text-slate-400',
                      )}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom hours table */}
              {mode === 'custom' && (
                <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-900 shadow-sm">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                      <tr>
                        <th className="px-4 py-2 font-medium text-slate-600 dark:text-slate-400 w-20 text-xs uppercase tracking-wider">
                          {t('manager.schedule.table.status')}
                        </th>
                        <th className="px-4 py-2 font-medium text-slate-600 dark:text-slate-400 w-32 text-xs uppercase tracking-wider">
                          {t('manager.schedule.table.day')}
                        </th>
                        <th className="px-4 py-2 font-medium text-slate-600 dark:text-slate-400 text-xs uppercase tracking-wider">
                          {t('manager.schedule.table.hours')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {WEEKDAYS.map((day) => {
                        const active = isDayActive(day)
                        return (
                          <tr
                            key={day}
                            className={cn(
                              'transition-colors',
                              active
                                ? 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                                : 'bg-slate-50/50 dark:bg-slate-800/20 text-slate-400 dark:text-slate-500',
                            )}
                          >
                            <td className="px-4 py-2">
                              <Switch
                                checked={active}
                                onCheckedChange={() => toggleDay(day)}
                                className="data-[state=checked]:bg-blue-600"
                              />
                            </td>
                            <td
                              className={cn(
                                'px-4 py-2 font-medium text-sm',
                                active && 'text-slate-900 dark:text-slate-100',
                              )}
                            >
                              {t(`manager.schedule.days.${day}`)}
                            </td>
                            <td className="px-4 py-2">
                              {active ? (
                                <div className="flex items-center gap-2">
                                  <select
                                    {...register(`days.${day}.open`)}
                                    className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white text-xs rounded-md focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 py-1 px-2 w-[110px]"
                                  >
                                    {TIME_OPTIONS.map((opt) => (
                                      <option key={`open-${day}-${opt}`} value={opt}>
                                        {formatHeure(opt, i18n.language)}
                                      </option>
                                    ))}
                                  </select>
                                  <span className="text-slate-400 text-sm">-</span>
                                  <select
                                    {...register(`days.${day}.close`)}
                                    className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white text-xs rounded-md focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 py-1 px-2 w-[110px]"
                                  >
                                    {TIME_OPTIONS.map((opt) => (
                                      <option key={`close-${day}-${opt}`} value={opt}>
                                        {formatHeure(opt, i18n.language)}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              ) : (
                                <span className="text-xs">
                                  {t('manager.schedule.unavailable')}
                                </span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

            </>
          )}
        </div>
      </div>
    </form>
  )
}
